import { memo, useMemo } from "react";
import type { EChartsOption, SeriesOption } from "echarts";
import EChart from "./EChart";
import { defaultAxisLabel, defaultGrid, tooltipCss } from "./chartDefaults";
import type { Timeseries } from "../../types";
import type { AxisFormatter } from "./XmrChart";

interface TimeseriesLineChartProps {
  series: Timeseries;
  axisFormatter?: AxisFormatter;
  axisName?: string;
}

function TimeseriesLineChart({ series, axisFormatter, axisName }: TimeseriesLineChartProps) {
  const option = useMemo<EChartsOption>(
    () => buildOption(series, axisFormatter, axisName),
    [series, axisFormatter, axisName],
  );
  return <EChart option={option} height={400} />;
}

function buildOption(series: Timeseries, axisFormatter?: AxisFormatter, axisName?: string): EChartsOption {
  const timestamps = series.timestamps.map(timestamp => timestamp.getTime());
  const data = timestamps.map((ts, idx) => [ts, series.data[idx]]);

  const lineSeries: SeriesOption = {
    name: "Value",
    type: "line",
    data,
    symbol: "circle",
    symbolSize: 5,
    lineStyle: { color: "#1D1D1F" },
    itemStyle: { color: "#1D1D1F" },
    smooth: true,
  };

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross" },
      extraCssText: tooltipCss,
      valueFormatter: axisFormatter ? value => axisFormatter(Number(value)) : undefined,
    },
    xAxis: {
      type: "time",
      boundaryGap: false,
    },
    yAxis: {
      type: "value",
      axisLabel: { formatter: axisFormatter ?? defaultAxisLabel, margin: 12 },
      name: axisName,
      nameLocation: "end",
      nameGap: 30,
    },
    grid: defaultGrid,
    series: [lineSeries],
  } satisfies EChartsOption;
}

export default memo(TimeseriesLineChart);
